import React, {FC, memo, useEffect, useMemo, useState} from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import TextField from '@pixel/core/TextField'
import useAutomationFormDataQuery, { DEFAULT_AUTOMATION_FORM_DATA, FilterField } from '../hooks/useAutomationFormDataQuery'
import {MenuItem, Stack } from '@mui/material'
import AutomationFieldQuery from './AutomationFieldQuery'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import { renderLocalizedMenuItems } from '../../components/LocalizedMenuItem'
import {useLocalizedText, useMultipleLocalizedText} from "../../contexts/Localization/localizationHooks";

interface ConditionInputProps {
    id?: string
    conditionIndex: number,
    children: any
    error: boolean
    errorMessage: string
}
const ConditionInput: FC<ConditionInputProps> = ({
    id,
    conditionIndex,
    children,
    error,
    errorMessage
}) => {
    const { control } = useFormContext()

    const { data } = useAutomationFormDataQuery({ onSuccess: () => { } })

    const { FILTER_FIELDS } = data

    const selectedTrigger = useWatch({ name: `triggerType`, control })

    const [requiredFields, setRequiredFields] = useState<Array<string>>([])

    const localizedFilterFields = useMemo(() =>
            FILTER_FIELDS.map(filter => ({
                text: filter.localeKey,
                value: filter.filterField,
            })),
        [FILTER_FIELDS])

    const {
        AUTOMATION_FORM_CONDITION,
        COMMON_AND,
    } = useMultipleLocalizedText()

    useEffect(() => {
        if (FILTER_FIELDS) {
            const reqs = FILTER_FIELDS
                .filter(el => el.required || (el.requiredOnTriggerType !== null && el.requiredOnTriggerType == selectedTrigger ) )
                .map(el => el.filterField)
            setRequiredFields(reqs)
        }
    }, [FILTER_FIELDS])

    return (
        <Stack direction="column" sx={{ width: '100%' }}>
            <Controller
                control={control}
                name={`companyFilters.${conditionIndex}.filterField`}
                rules={{ required: true }}
                render={({ field, fieldState, }) => (
                    <TextField
                        id={id}
                        select
                        required
                        label={conditionIndex === 0 ? AUTOMATION_FORM_CONDITION : COMMON_AND}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        error={error || fieldState.invalid}
                        errorText={error ? errorMessage : ''}
                        fullWidth
                        disabled={requiredFields.includes(field.value)}
                        augmentField={() => (
                            requiredFields.includes(field.value) ? null : <>{children}</>
                        )}
                    >

                        {
                         localizedFilterFields.map(renderLocalizedMenuItems)
                        }

                    </TextField>
                )}
            />
            <ConditionValueInputs conditionIndex={conditionIndex} />
        </Stack>
    )
}

interface ConditionValueInputsProps {
    conditionIndex: number
}
const ConditionValueInputs: FC<ConditionValueInputsProps> = ({
    conditionIndex,
}) => {
    const { control, setValue } = useFormContext()
    const selectedCondition = useWatch({ name: `companyFilters.${conditionIndex}.filterField`, control })

    const { data } = useAutomationFormDataQuery({ onSuccess: () => { } })
    const { FILTER_FIELDS } = data || DEFAULT_AUTOMATION_FORM_DATA

    const [firstRender, setFirstRender] = useState(true)

    const foundCondition = useMemo(() => FILTER_FIELDS.find(condition => condition.filterField === selectedCondition), [FILTER_FIELDS, selectedCondition])

    useEffect(() => {
        setValue(`companyFilters.${conditionIndex}.sortOrder`, conditionIndex);
    }, [conditionIndex, setValue])

    useEffect(() => {
        if (!foundCondition?.statementOperatorRequired) {
            setValue(`companyFilters.${conditionIndex}.statementOperator`, 'EMPTY');
        }
    }, [foundCondition, setValue]);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
        } else {
            setValue(`companyFilters.${conditionIndex}.companyFilterValues`, []);
        }
    }, [selectedCondition, setValue]);

    const anyText = useLocalizedText(LocalizedText.AUTOMATION_FILTER_OPERATOR_ONE);
    const noneText = useLocalizedText(LocalizedText.AUTOMATION_FILTER_OPERATOR_NOT);

    const translatedOperators = useMemo(() => {
        const operators = [];
        if (!foundCondition?.unsupportedOperatorValues?.includes('ANY')) {
            operators.push({ value: 'ANY', text: anyText });
        }
        if (!foundCondition?.unsupportedOperatorValues?.includes('NONE')) {
            operators.push({ value: 'NONE', text: noneText });
        }
        return operators;
    }, [anyText, noneText, foundCondition]);

    if (foundCondition) {

        return (
            <>
                <Controller
                    control={control}
                    name={`companyFilters.${conditionIndex}.statementOperator`}
                    render={({ field, fieldState, }) => (
                        <TextField
                            id={`automation-form-condition-field-${conditionIndex}-statement-field`}
                            select
                            required
                            label=""
                            {...field}
                            error={fieldState.invalid}
                            errorText={fieldState?.error?.message || ''}
                            fullWidth
                            disabled={!foundCondition}
                            sx={{ marginTop: 1 }}
                            hidden={!foundCondition.statementOperatorRequired}

                        >
                            {translatedOperators.map((operator, index) => (
                                <MenuItem key={index} value={operator.value}>
                                    {operator.text}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
                <ConditionValueMultiSelect
                    id={`automation-form-condition-field-${conditionIndex}-values-field`}
                    conditionIndex={conditionIndex}
                    conditionSelected={foundCondition}
                />
                <Controller
                    control={control}
                    name={`companyFilters.${conditionIndex}.sortOrder`}
                    defaultValue={conditionIndex}
                    render={({ field, }) => (
                        <TextField
                            value={conditionIndex}
                            {...field}
                            hidden={true}
                        >
                        </TextField>
                    )}
                />
            </>
        )
    }

    return (
        <ConditionValueMultiSelect
            id={`automation-form-condition-field-${conditionIndex}-values-field`}
            conditionIndex={conditionIndex}
            conditionSelected={foundCondition}
        />
    )
}

interface ConditionValueMultiSelectProps {
    id?: string
    conditionIndex: number
    conditionSelected: FilterField
}
const ConditionValueMultiSelect: FC<ConditionValueMultiSelectProps> = ({
    id,
    conditionIndex,
    conditionSelected,
}) => {
    const { control, resetField } = useFormContext()

    const automationType = useWatch({ name: `automationType`, control })

    const triggerType = useWatch({ name: `triggerType`, control })

    if (!conditionSelected) return null

    return (
        <>
            <Controller
                control={control}
                name={`companyFilters.${conditionIndex}.companyFilterValues`}
                render={({ field, }) => {
                    return (
                        <AutomationFieldQuery
                            id={id}
                            companyAutomation={control._formValues as any}
                            fieldEId={conditionSelected.filterField}
                            conditionSelected={conditionSelected}
                            automationType={automationType}
                            triggerType={triggerType}
                            value={field.value}
                            onChange={(e, value) => field.onChange(value)}
                            onReset={() => {
                                resetField(`companyFilters.${conditionIndex}.companyFilterValues`)
                            }}
                        />
                    )
                }
            }
            />
        </>
    )
}

export default memo(ConditionInput)
